import { Venta, VentaItem } from "@/shared/types/venta";
import { Stack, StackProps, Typography } from "@mui/material";
import dayjs from "dayjs";
import { ReactNode } from "react";
import FacturaThemeProvider from "./factura-theme-provider";
import { DataTable } from "@/components/data-table";
import { formatNumber, formatString } from "@/shared/formats";
import { capitalize, numeroALetras } from "../../shared/utils";
import { useConfigurationContext } from "@/features/configuration/contexts/configuration-context";
import useMonetizacion from "@/features/monetizacion/hooks/use-monetizacion";
import { mapMonedaValues } from "@/features/monetizacion/shared/utils";

export interface VentaInfoHorizontalProps extends StackProps {
    venta: Venta,
    items: VentaItem[]
}

export default function VentaInfoHorizontal({
    venta,
    items,
    ...props
}: VentaInfoHorizontalProps) {
    const monedaBase = useMonetizacion().monedaBase;
    const cotizacion = venta.cotizacion;
    const { showMonedaId, printPaddingRight, articuloNombreMaxLength } = useConfigurationContext();
    const truncateNombre = (nombre: string) => articuloNombreMaxLength > 0 && nombre.length > articuloNombreMaxLength ? nombre.slice(0, articuloNombreMaxLength) + "..." : nombre;

    const total = mapMonedaValues(cotizacion, venta.total, monedaBase.id, showMonedaId);
    const parcialExenta = mapMonedaValues(cotizacion, venta.total, monedaBase.id, showMonedaId);
    const parcialIvaCinco = mapMonedaValues(cotizacion, venta.total, monedaBase.id, showMonedaId);
    const parcialIvaDiez = mapMonedaValues(cotizacion, venta.total, monedaBase.id, showMonedaId);
    const liquidacionIvaCinco = mapMonedaValues(cotizacion, venta.total, monedaBase.id, showMonedaId);
    const liquidacionIvaDiez = mapMonedaValues(cotizacion, venta.total, monedaBase.id, showMonedaId);
    const liquidacionIvaTotal = mapMonedaValues(cotizacion, venta.total, monedaBase.id, showMonedaId);


    return <FacturaThemeProvider>
        <Stack gap={2} padding={3} paddingRight={`${printPaddingRight}mm`} {...props}>
            {/* Header */}
            <Stack gap={1}>
                <AttributeValuePair label="Fecha: " value={dayjs(venta.fecha).format("DD [de] MMMM [del] YYYY - HH:mm")} />
                {venta.presupuesto ?
                    <AttributeValuePair label="Presupuesto" value="" /> :
                    <AttributeValuePair label="Condición de Venta: " value={venta.credito ? "Crédito" : "Contado"} />}
                <AttributeValuePair label="Nombre y/o Razón Social: " value={venta.clienteNombre} />
                <AttributeValuePair label="RUC: " value={venta.clienteNumDocumento} />
            </Stack>

            <DashedDivider />

            {/* Body */}
            <DataTable flex={1} minHeight={"100px"}>
                <DataTable.Content
                    fit="flex"
                    rows={items}
                    columns={[
                        {
                            key: "cantidad",
                            header: <Typography variant="body1">CANT.</Typography>,
                            render: row => <Typography variant="body1">{formatNumber(row.cantidad)}</Typography>,
                            width: "auto"
                        },
                        {
                            key: "nombre",
                            header: <Typography variant="body1">DESCRIPCIÓN</Typography>,
                            render: row => <Typography variant="body1">{formatString(truncateNombre(row.articuloNombre))}</Typography>
                        },
                        {
                            key: "iva",
                            header: <Typography variant="body1">IVA (%)</Typography>,
                            render: row => <Typography variant="body1">{formatNumber(row.articuloIva)}</Typography>,
                            width: "auto",
                            align: "right"
                        },
                        {
                            key: "precio",
                            header: <Typography variant="body1">P. UNITARIO</Typography>,
                            render: row => <Typography variant="body1">{formatNumber(mapMonedaValues(cotizacion, row.precioUnitario, monedaBase.id, showMonedaId))}</Typography>,
                            width: "auto",
                            align: "right"
                        },
                        {
                            key: "subtotal",
                            header: <Typography variant="body1">SUBTOTAL</Typography>,
                            render: row => <Typography variant="body1">{formatNumber(mapMonedaValues(cotizacion, row.precioUnitario * row.cantidad, monedaBase.id, showMonedaId))}</Typography>,
                            width: "auto",
                            align: "right"
                        },
                    ]}
                />
            </DataTable>

            <DashedDivider />

            <Stack gap={1}>
                <Typography variant="body1">{capitalize(numeroALetras(total))}</Typography>
            </Stack>

            <Stack gap={1}>
                <AttributeValuePair label="Parcial Exenta: " value={formatNumber(parcialExenta)} />
                <AttributeValuePair label="Parcial 5%:" value={formatNumber(parcialIvaCinco)} />
                <AttributeValuePair label="Parcial 10%: " value={formatNumber(parcialIvaDiez)} />
                <AttributeValuePair variant="bold" marginLeft={5} label="Total a Pagar: " value={formatNumber(total)} />
            </Stack>

            <Stack gap={1}>
                <AttributeValuePair label="Liq. IVA 5%: " value={formatNumber(liquidacionIvaCinco)} />
                <AttributeValuePair label="Liq. IVA 10%: " value={formatNumber(liquidacionIvaDiez)} />
                <AttributeValuePair variant="bold" marginLeft={5} label="Total IVA: " value={formatNumber(liquidacionIvaTotal)} />
            </Stack>
        </Stack>
    </FacturaThemeProvider>
}

function AttributeValuePair({
    label,
    value,
    variant = "normal",
    ...props
}: {
    label?: ReactNode,
    value?: ReactNode,
    variant?: "normal" | "bold"
} & StackProps) {
    return <Stack direction={"row"} gap={5} {...props}>
        <Typography fontWeight={variant} variant={"body1"}>{label}</Typography>
        <Typography fontWeight={variant} variant={"body1"}>{value == null ? "---" : value}</Typography>
    </Stack>
}

function DashedDivider() {
    return <Stack borderBottom={"1px dashed #000000"} />
}